import { useState } from "react";
import {
  Meal,
  MealPlanPreviewProps,
  Recipe,
  isApiIngredient,
} from "types/mealPlans";

import ExportButtons from "./ExportButtons";
import InstacartButton from "./InstacartButton";

import Button from "components/atoms/Button";

import { formatIngredient } from "utils/ingredientUtils";

const MealPlanPreview: React.FC<MealPlanPreviewProps> = ({
  mealPlanData,
  onRejectRecipe,
  onGenerateFull,
  onBack,
  disableGenerate = false,
  showFullDetails = false,
  isLoading = false,
  onImportMealPlan,
  apiClient,
}) => {
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  // Safety check for missing meal plan data
  if (
    !mealPlanData ||
    !mealPlanData.mealPlanDays ||
    !Array.isArray(mealPlanData.mealPlanDays) ||
    mealPlanData.mealPlanDays.length === 0
  ) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">
            {showFullDetails ? "Complete Meal Plan" : "Meal Plan Preview"}
          </h3>
        </div>
        <div className="p-4 border border-gray-200 rounded-md text-gray-500">
          No meal plan data available. Please try again.
        </div>
      </div>
    );
  }

  const totalDays = mealPlanData.mealPlanDays.length;
  // Ensure currentDayIndex is valid
  const safeCurrentDayIndex = currentDayIndex < totalDays ? currentDayIndex : 0;
  const currentDay = mealPlanData.mealPlanDays[safeCurrentDayIndex];

  const handleDaySelect = (index: number) => {
    if (index >= 0 && index < totalDays) {
      setCurrentDayIndex(index);
    }
  };

  // Format ingredient for display, handling both API and UI formats
  // Now using the utility function

  // Function to get recipe nutrition
  const getRecipeNutrition = (recipe: Recipe) => {
    if (!recipe) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    }

    if (recipe.macros) {
      return recipe.macros;
    }

    if (recipe.totalNutrition) {
      return {
        calories: recipe.totalNutrition.calories,
        protein: recipe.totalNutrition.protein,
        carbs: recipe.totalNutrition.carbs,
        fat: recipe.totalNutrition.fat,
        fiber: recipe.totalNutrition.fiber || 0, // Use fiber if available, default to 0
      };
    }

    // Calculate from ingredients if needed
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
    };

    // Check if ingredients exist before looping
    if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
      recipe.ingredients.forEach((ingredient) => {
        if (!ingredient) return;

        if (isApiIngredient(ingredient)) {
          if (ingredient.nutritionPreview) {
            totals.calories += ingredient.nutritionPreview.calories || 0;
            totals.protein += ingredient.nutritionPreview.protein || 0;
            totals.carbs += ingredient.nutritionPreview.carbs || 0;
            totals.fat += ingredient.nutritionPreview.fat || 0;
            totals.fiber += ingredient.nutritionPreview.fiber || 0;
          }
        } else {
          totals.calories += ingredient.calories || 0;
          totals.protein += ingredient.protein || 0;
          totals.carbs += ingredient.carbs || 0;
          totals.fat += ingredient.fat || 0;
          totals.fiber += ingredient.fiber || 0;
        }
      });
    }

    return totals;
  };

  // Function to get meal nutrition
  const getMealNutrition = (meal: Meal) => {
    if (!meal) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    }

    if (meal.totalNutrition) {
      return {
        calories: meal.totalNutrition.calories,
        protein: meal.totalNutrition.protein,
        carbs: meal.totalNutrition.carbs,
        fat: meal.totalNutrition.fat,
        fiber: meal.totalNutrition.fiber || 0, // Use fiber if available
      };
    }

    // Calculate from recipes
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
    };

    // Check if recipes exist before looping
    if (meal.recipes && Array.isArray(meal.recipes)) {
      meal.recipes.forEach((recipe) => {
        const nutrition = getRecipeNutrition(recipe);
        totals.calories += nutrition.calories || 0;
        totals.protein += nutrition.protein || 0;
        totals.carbs += nutrition.carbs || 0;
        totals.fat += nutrition.fat || 0;
        totals.fiber += nutrition.fiber || 0;
      });
    }

    return totals;
  };

  // Function to get day nutrition
  const getDayNutrition = (day: typeof currentDay) => {
    if (!day) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    }

    // First check for macros property from API
    if (day.macros) {
      return {
        calories: day.macros.calories,
        protein: day.macros.protein,
        carbs: day.macros.carbs,
        fat: day.macros.fat,
        fiber: day.macros.fiber || 0, // Use fiber if available
      };
    }

    if (day.totalNutrition) {
      return {
        calories: day.totalNutrition.calories,
        protein: day.totalNutrition.protein,
        carbs: day.totalNutrition.carbs,
        fat: day.totalNutrition.fat,
        fiber: day.totalNutrition.fiber || 0, // Use fiber if available
      };
    }

    // Calculate from meals
    const totals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
    };

    ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
      if (day[mealType as keyof typeof day]) {
        const meal = day[mealType as keyof typeof day] as Meal;
        const nutrition = getMealNutrition(meal);
        totals.calories += nutrition.calories || 0;
        totals.protein += nutrition.protein || 0;
        totals.carbs += nutrition.carbs || 0;
        totals.fat += nutrition.fat || 0;
        totals.fiber += nutrition.fiber || 0;
      }
    });

    return totals;
  };

  // Calculate current day nutrition with safety check
  const currentDayNutrition = currentDay
    ? getDayNutrition(currentDay)
    : { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };

  const renderMeal = (
    meal: Meal,
    mealType: "breakfast" | "lunch" | "dinner" | "snack"
  ) => {
    // Safety check for missing meal or recipes
    if (!meal || !meal.recipes || !Array.isArray(meal.recipes)) {
      return (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-gray-900 capitalize mb-3">
            {mealType}
          </h4>
          <div className="p-4 border border-gray-200 rounded-md text-gray-500">
            No recipes available for this meal.
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 capitalize mb-3">
          {mealType}
        </h4>
        <div className="space-y-4">
          {meal.recipes.map((recipe, recipeIndex) => (
            <div
              key={`${mealType}-${recipeIndex}`}
              className="p-4 border border-gray-200 rounded-md"
            >
              <div className="flex justify-between items-start">
                <h5
                  className={`text-md font-medium ${
                    recipe.rejected
                      ? "line-through text-gray-400"
                      : "text-gray-800"
                  }`}
                >
                  {recipe.name || "Unnamed recipe"}
                </h5>
                {onRejectRecipe && !showFullDetails && (
                  <button
                    onClick={() =>
                      onRejectRecipe(safeCurrentDayIndex, mealType, recipeIndex)
                    }
                    className={`text-sm font-medium px-2 py-1 rounded-md ${
                      recipe.rejected
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-red-100 text-red-700 hover:bg-red-200"
                    } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={isLoading}
                  >
                    {recipe.rejected ? "Undo Reject" : "Reject"}
                  </button>
                )}
              </div>

              {showFullDetails &&
                recipe.ingredients &&
                recipe.ingredients.length > 0 && (
                  <div className="mt-3">
                    <h6 className="text-sm font-medium text-gray-700 mb-1">
                      Ingredients:
                    </h6>
                    <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                      {recipe.ingredients.map((ingredient, idx) => (
                        <li key={idx}>{formatIngredient(ingredient)}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {showFullDetails && recipe.preparation && (
                <div className="mt-3">
                  <h6 className="text-sm font-medium text-gray-700 mb-1">
                    Preparation:
                  </h6>
                  <div className="text-sm text-gray-600 space-y-1">
                    {recipe.preparation}
                  </div>
                </div>
              )}

              {/* Show macros if available */}
              {showFullDetails && recipe.macros && (
                <div className="mt-3 pt-2 border-t border-gray-100">
                  <div className="flex justify-end">
                    <span className="text-sm font-medium">
                      Recipe Total:{" "}
                      <span className="text-sm text-gray-600">
                        <span className="font-medium">Calories:</span>{" "}
                        {recipe.macros.calories.toFixed(0)} kcal |
                        <span className="font-medium"> Carbs:</span>{" "}
                        {recipe.macros.carbs.toFixed(0)}g |
                        <span className="font-medium"> Fat:</span>{" "}
                        {recipe.macros.fat.toFixed(0)}g |
                        <span className="font-medium"> Protein:</span>{" "}
                        {recipe.macros.protein.toFixed(0)}g |
                        <span className="font-medium"> Fiber:</span>{" "}
                        {(recipe.macros.fiber || 0).toFixed(0)}g
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const hasRejectedRecipes = () => {
    return mealPlanData.mealPlanDays.some(
      (day) =>
        day.breakfast.recipes.some((recipe) => recipe.rejected) ||
        day.lunch.recipes.some((recipe) => recipe.rejected) ||
        day.dinner.recipes.some((recipe) => recipe.rejected) ||
        day.snack.recipes.some((recipe) => recipe.rejected)
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">
          {showFullDetails ? "Complete Meal Plan" : "Meal Plan Preview"}
        </h3>
        {onBack && <Button text="Back to Form" onClick={onBack} outlined />}
      </div>

      {/* Overlay the entire component with a semi-transparent disabled state when loading */}
      <div
        className={`relative ${
          isLoading ? "pointer-events-none opacity-50" : ""
        }`}
      >
        {/* Nutrition Summary */}
        <div className="mb-6 p-4 bg-gray-50 rounded-md">
          <h4 className="text-lg font-medium text-gray-900 mb-3">
            {showFullDetails
              ? `Day ${safeCurrentDayIndex + 1} Nutrition Summary`
              : "Daily Nutrition Target Summary"}
          </h4>

          {showFullDetails ? (
            // Show actual nutrition for full meal plan
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500">Calories</p>
                <p className="text-lg font-medium">
                  {Math.round(currentDayNutrition.calories)} kcal
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Protein</p>
                <p className="text-lg font-medium">
                  {Math.round(currentDayNutrition.protein)}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Carbs</p>
                <p className="text-lg font-medium">
                  {Math.round(currentDayNutrition.carbs)}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fat</p>
                <p className="text-lg font-medium">
                  {Math.round(currentDayNutrition.fat)}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fiber</p>
                <p className="text-lg font-medium">
                  {Math.round(currentDayNutrition.fiber)}g
                </p>
              </div>
            </div>
          ) : // Show target nutrition for preview
          mealPlanData.constraints?.macros ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500">Calories</p>
                <p className="text-lg font-medium">
                  {mealPlanData.constraints.macros.calories} kcal
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Protein</p>
                <p className="text-lg font-medium">
                  {mealPlanData.constraints.macros.protein}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Carbs</p>
                <p className="text-lg font-medium">
                  {mealPlanData.constraints.macros.carbs}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fat</p>
                <p className="text-lg font-medium">
                  {mealPlanData.constraints.macros.fat}g
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fiber</p>
                <p className="text-lg font-medium">
                  {mealPlanData.constraints.macros.fiber}g
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">
              No specific macro targets provided. The AI will generate a
              balanced meal plan based on your profile and preferences.
            </p>
          )}
        </div>

        {/* Day tabs navigation */}
        <div className="mb-6">
          <div className="flex border-b border-gray-200">
            {mealPlanData.mealPlanDays.map((_, index) => (
              <button
                key={`day-tab-${index}`}
                onClick={() => handleDaySelect(index)}
                disabled={isLoading}
                className={`py-2 px-4 font-medium text-sm ${
                  safeCurrentDayIndex === index
                    ? "border-b-2 border-passio-indigo text-passio-indigo"
                    : "text-gray-500 hover:text-gray-700"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Day {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Meal plan content */}
        <div>
          {currentDay && (
            <>
              {renderMeal(currentDay.breakfast, "breakfast")}
              {renderMeal(currentDay.lunch, "lunch")}
              {renderMeal(currentDay.dinner, "dinner")}
              {renderMeal(currentDay.snack, "snack")}
            </>
          )}
        </div>

        {/* Export buttons for full meal plan */}
        {showFullDetails && (
          <>
            <ExportButtons
              mealPlanData={mealPlanData}
              onImportMealPlan={onImportMealPlan}
            />
            {apiClient && (
              <InstacartButton
                mealPlanData={mealPlanData}
                apiClient={apiClient}
              />
            )}
          </>
        )}

        {/* Action buttons */}
        {!showFullDetails && onGenerateFull && (
          <div className="flex justify-center mt-8">
            <Button
              text="Generate Full Meal Plan"
              onClick={onGenerateFull}
              disabled={disableGenerate || isLoading}
              isLoading={isLoading}
              fullWidth
            />
          </div>
        )}

        {disableGenerate && !isLoading && (
          <p className="text-center text-red-600 mt-2">
            Please approve all recipes before generating the full meal plan.
          </p>
        )}

        {isLoading && (
          <p className="text-center text-gray-600 mt-2">
            {hasRejectedRecipes()
              ? "Regenerating meal plan with your preferences... Please wait."
              : "Generating meal plan... Please wait."}
          </p>
        )}
      </div>
    </div>
  );
};

export default MealPlanPreview;
